import React, { useEffect, useRef } from 'react';
import { Animated, Switch, type SwitchProps } from 'react-native';

interface AnimatedLoadingSwitchProps extends Omit<SwitchProps, 'value'> {
  /**
   * Whether the switch is currently loading initial data
   */
  isLoading: boolean;
  /**
   * The target value once loading is complete
   */
  targetValue: boolean;
  /**
   * Track color configuration
   */
  trackColor?: { true: string; false: string };
  /**
   * Thumb color
   */
  thumbColor?: string;
}

/**
 * AnimatedLoadingSwitch Component
 *
 * A switch that starts in the "off" position during initial load,
 * then animates to the target value once data is loaded.
 *
 * @remarks
 * - Shows disabled switch in "off" position while loading
 * - Animates to target value with spring animation once loaded
 * - Inherits all standard Switch props except 'value'
 */
export function AnimatedLoadingSwitch({
  isLoading,
  targetValue,
  trackColor = { true: '#34C759', false: '#d4d4d8' },
  thumbColor = '#ffffff',
  disabled,
  ...switchProps
}: AnimatedLoadingSwitchProps) {
  const animatedValue = useRef(new Animated.Value(0)).current;
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (isLoading) {
      // Reset to off position when loading starts
      hasLoadedRef.current = false;
      animatedValue.setValue(0);
    } else if (!hasLoadedRef.current) {
      // Animate to target value once loaded
      hasLoadedRef.current = true;

      Animated.spring(animatedValue, {
        toValue: targetValue ? 1 : 0,
        useNativeDriver: false,
        tension: 50,
        friction: 7,
      }).start();
    }
  }, [isLoading, targetValue, animatedValue]);

  // During loading: show off state, disabled
  if (isLoading) {
    return (
      <Switch
        {...switchProps}
        value={false}
        disabled={true}
        trackColor={trackColor}
        thumbColor={thumbColor}
      />
    );
  }

  // After loading: show actual value
  return (
    <Switch
      {...switchProps}
      value={targetValue}
      disabled={disabled}
      trackColor={trackColor}
      thumbColor={thumbColor}
    />
  );
}
